/**
 * uvUtils.ts — UV coordinate generation for geometries without UV data.
 *
 * STL files (the primary geometry format from OpenSCAD) do not include UV
 * coordinates. Three.js requires a `uv` BufferAttribute for texture mapping
 * (`material.map`) to work. This module provides box-projection UV generation
 * which works well for the flat-faced geometry OpenSCAD produces.
 *
 * Algorithm (box / triplanar projection):
 *   For each triangle face:
 *     1. Compute the face normal from the cross product of two edges.
 *     2. Determine the dominant axis (the axis with the largest absolute normal component).
 *     3. Project the vertex positions onto the two non-dominant axes.
 *     4. Normalize the projected coordinates to [0, 1] using the geometry's bounding box.
 *
 * This produces correct, non-overlapping UVs for axis-aligned flat faces
 * (cubes, cylinders, polyhedra, etc.) — exactly the geometry OpenSCAD outputs.
 * Curved surfaces may show seams at axis transitions, but this is acceptable
 * for procedurally generated textures.
 *
 * Face-constrained UVs (generateFaceConstrainedUVs):
 *   For textures that should only appear on one face direction (e.g. "top",
 *   "front"), this function generates a secondary UV attribute (default "uv1")
 *   where triangles whose outward normal points in the requested direction get
 *   normal [0,1] box-projection UVs, and all other triangles get UV (0,0) —
 *   a guaranteed-transparent 2×2 corner region reserved in the texture canvas.
 *   The Three.js texture's `.channel` property is set to 1 so it reads from
 *   `uv1` rather than the primary `uv` attribute.
 *
 * Hot path acceleration:
 *   The inner loop is compiled to WebAssembly via AssemblyScript
 *   (src/as/assembly/index.ts).  The WASM module is loaded once at module
 *   initialisation time; if it fails to load (old browser, SSR, test
 *   environment) the code falls back to the pure-JS implementation below.
 *   The JS fallback is never removed — it ensures correctness in all envs.
 */

import * as THREE from 'three';
import type { BoxUVWasmExports } from '@as/build/assembly';

// ---------------------------------------------------------------------------
// WASM module loading
// ---------------------------------------------------------------------------

/** Resolves to the WASM exports once the module is ready, or null on failure. */
let wasmExports: BoxUVWasmExports | null = null;

// Dynamic import so that a load failure is gracefully caught and the module
// can still be used synchronously via the JS fallback.
// @as is mapped to src/as in tsconfig.app.json; Vite resolves it to the
// hand-written assembly.js wrapper (which streams assembly.wasm).
import('@as/build/assembly')
  .then((mod) => mod.default) // default export is the instantiation Promise
  .then((exports) => {
    wasmExports = exports;
  })
  .catch(() => {
    // WASM unavailable — JS fallback will be used for all calls.
  });

// ---------------------------------------------------------------------------
// WASM memory helper
// ---------------------------------------------------------------------------

const WASM_PAGE_BYTES = 65536; // 64 KiB per WebAssembly memory page

/**
 * Ensure the WASM module's linear memory is large enough to hold `byteCount`
 * bytes starting at byte offset `offset`, then return a fresh DataView over
 * the (possibly grown) buffer.
 */
function ensureMemory(
  mem: WebAssembly.Memory,
  offset: number,
  byteCount: number,
): DataView {
  const needed = offset + byteCount;
  if (mem.buffer.byteLength < needed) {
    const additionalPages = Math.ceil(
      (needed - mem.buffer.byteLength) / WASM_PAGE_BYTES,
    );
    mem.grow(additionalPages);
  }
  return new DataView(mem.buffer);
}

// ---------------------------------------------------------------------------
// Layout constants for the two arrays in WASM linear memory
//
// We place the position data at the first byte after the AS runtime's
// reserved region.  The AS debug build reserves 32 KiB (32 768 bytes) for
// its own bookkeeping; we start just past that so we never overlap the
// runtime's stack or heap metadata.
//
// Layout (all at fixed offsets, no dynamic allocation needed):
//   [0 … POS_OFFSET)       : AS runtime reserved region
//   [POS_OFFSET … UV_START): position data  (vertexCount × 12 bytes)
//   [UV_START …)            : UV data        (vertexCount × 8 bytes)
//
// UV_START is computed per-call since it depends on vertex count.
// ---------------------------------------------------------------------------

const POS_OFFSET = 32768; // 32 KiB — safely past the AS runtime reserved area

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Generate box-projection UV coordinates for a BufferGeometry.
 *
 * Mutates the geometry in-place by setting the `uv` attribute.
 * If the geometry already has UVs, this is a no-op.
 *
 * Uses the AssemblyScript/WASM fast path when available; falls back to the
 * pure-JS implementation otherwise.
 *
 * @param geometry - The BufferGeometry to add UVs to.
 */
export function generateBoxProjectionUVs(geometry: THREE.BufferGeometry): void {
  // Skip if UVs already exist
  if (geometry.attributes.uv) return;

  const posAttr = geometry.attributes.position;
  if (!posAttr) return;

  const vertexCount = posAttr.count;
  if (vertexCount === 0) return;

  // Compute bounding box for normalization
  if (!geometry.boundingBox) {
    geometry.computeBoundingBox();
  }
  const box = geometry.boundingBox!;
  const size = new THREE.Vector3();
  box.getSize(size);

  // Avoid division by zero for degenerate dimensions
  const sx = size.x > 1e-6 ? size.x : 1;
  const sy = size.y > 1e-6 ? size.y : 1;
  const sz = size.z > 1e-6 ? size.z : 1;

  if (wasmExports !== null) {
    // -----------------------------------------------------------------------
    // Fast path: WASM
    // -----------------------------------------------------------------------
    const mem = wasmExports.memory;

    // Byte sizes of the two arrays
    const posByteLen = vertexCount * 12; // 3 × f32 per vertex
    const uvByteLen = vertexCount * 8; //  2 × f32 per vertex
    const uvOffset = POS_OFFSET + posByteLen;

    // Make sure the linear memory is large enough
    ensureMemory(mem, POS_OFFSET, posByteLen + uvByteLen);

    // Write position data into WASM memory as a flat f32 array.
    // posAttr.array is already a Float32Array (STLLoader always produces one),
    // so we can copy it in one shot with a typed array view.
    const posArray = posAttr.array as Float32Array;
    new Float32Array(mem.buffer, POS_OFFSET, vertexCount * 3).set(posArray);

    // Run the WASM kernel
    wasmExports.generateBoxUVs(
      POS_OFFSET,
      uvOffset,
      vertexCount,
      box.min.x,
      box.min.y,
      box.min.z,
      sx,
      sy,
      sz,
    );

    // Read the UV results back out and attach them to the geometry.
    // We slice (copy) so the geometry owns its own Float32Array rather than
    // holding a view into WASM memory that could be invalidated by a future
    // memory.grow() call.
    const uvs = new Float32Array(
      mem.buffer.slice(uvOffset, uvOffset + uvByteLen),
    );
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  } else {
    // -----------------------------------------------------------------------
    // Fallback path: pure JS
    // -----------------------------------------------------------------------
    _generateBoxProjectionUVsJS(
      geometry,
      posAttr,
      vertexCount,
      box,
      sx,
      sy,
      sz,
    );
  }
}

// ---------------------------------------------------------------------------
// Face name → (axis, sign) mapping
// ---------------------------------------------------------------------------

/**
 * Maps a face name string to a dominant axis index and sign.
 *
 * Axis encoding: 0 = X, 1 = Y, 2 = Z
 * Sign encoding: +1 = positive normal, -1 = negative normal
 *
 * OpenSCAD's default orientation: Y is depth (front = -Y, back = +Y),
 * Z is up (top = +Z, bottom = -Z), X is right (right = +X, left = -X).
 *
 * Returns null for unknown or empty face names (meaning "all faces").
 */
export function parseFaceName(
  face: string,
): { axis: number; sign: number } | null {
  switch (face.toLowerCase()) {
    case 'top':
      return { axis: 2, sign: 1 }; // +Z
    case 'bottom':
      return { axis: 2, sign: -1 }; // -Z
    case 'front':
      return { axis: 1, sign: -1 }; // -Y (OpenSCAD default)
    case 'back':
      return { axis: 1, sign: 1 }; // +Y
    case 'right':
      return { axis: 0, sign: 1 }; // +X
    case 'left':
      return { axis: 0, sign: -1 }; // -X
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Face-constrained UV generation
// ---------------------------------------------------------------------------

/**
 * Generate face-constrained box-projection UVs for a geometry.
 *
 * Unlike `generateBoxProjectionUVs` (which writes to the primary "uv"
 * attribute and applies to all faces), this function writes to a separate UV
 * attribute (default "uv1") where only triangles whose outward normal points
 * in the given `face` direction receive real [0,1] box-projection UVs.
 * All other triangles get UV (0, 0) — pointing to the guaranteed-transparent
 * 2×2 pixel corner region that the texture system reserves.
 *
 * The caller is responsible for setting `texture.channel = 1` (or matching
 * the attribute index) so the material samples from this UV slot.
 *
 * Calling this function when `face` is empty/unknown is a no-op.
 *
 * @param geometry      - The BufferGeometry to add face-constrained UVs to.
 * @param face          - Face name: "top"|"bottom"|"front"|"back"|"left"|"right"
 * @param uvAttrName    - UV attribute name to write (default "uv1")
 */
export function generateFaceConstrainedUVs(
  geometry: THREE.BufferGeometry,
  face: string,
  uvAttrName: string = 'uv1',
): void {
  const parsed = parseFaceName(face);
  if (!parsed) return; // unknown/empty face — no-op

  const posAttr = geometry.attributes.position;
  if (!posAttr) return;

  const vertexCount = posAttr.count;
  if (vertexCount === 0) return;

  // Compute bounding box for normalisation
  if (!geometry.boundingBox) {
    geometry.computeBoundingBox();
  }
  const box = geometry.boundingBox!;
  const size = new THREE.Vector3();
  box.getSize(size);

  const sx = size.x > 1e-6 ? size.x : 1;
  const sy = size.y > 1e-6 ? size.y : 1;
  const sz = size.z > 1e-6 ? size.z : 1;

  const { axis: faceAxis, sign: faceSign } = parsed;

  if (wasmExports !== null) {
    // -----------------------------------------------------------------------
    // Fast path: WASM
    // -----------------------------------------------------------------------
    const mem = wasmExports.memory;
    const posByteLen = vertexCount * 12;
    const uvByteLen = vertexCount * 8;
    const uvOffset = POS_OFFSET + posByteLen;

    ensureMemory(mem, POS_OFFSET, posByteLen + uvByteLen);

    const posArray = posAttr.array as Float32Array;
    new Float32Array(mem.buffer, POS_OFFSET, vertexCount * 3).set(posArray);

    wasmExports.generateBoxUVsForFace(
      POS_OFFSET,
      uvOffset,
      vertexCount,
      box.min.x,
      box.min.y,
      box.min.z,
      sx,
      sy,
      sz,
      faceAxis,
      faceSign,
    );

    const uvs = new Float32Array(
      mem.buffer.slice(uvOffset, uvOffset + uvByteLen),
    );
    geometry.setAttribute(uvAttrName, new THREE.Float32BufferAttribute(uvs, 2));
  } else {
    // -----------------------------------------------------------------------
    // Fallback path: pure JS
    // -----------------------------------------------------------------------
    _generateFaceConstrainedUVsJS(
      geometry,
      posAttr,
      vertexCount,
      box,
      sx,
      sy,
      sz,
      faceAxis,
      faceSign,
      uvAttrName,
    );
  }
}

// ---------------------------------------------------------------------------
// Pure-JS fallback implementation
// (identical algorithm to the original uvUtils.ts)
// ---------------------------------------------------------------------------

function _generateBoxProjectionUVsJS(
  geometry: THREE.BufferGeometry,
  posAttr: THREE.BufferAttribute | THREE.InterleavedBufferAttribute,
  vertexCount: number,
  box: THREE.Box3,
  sx: number,
  sy: number,
  sz: number,
): void {
  const uvs = new Float32Array(vertexCount * 2);

  const vA = new THREE.Vector3();
  const vB = new THREE.Vector3();
  const vC = new THREE.Vector3();
  const edge1 = new THREE.Vector3();
  const edge2 = new THREE.Vector3();
  const normal = new THREE.Vector3();

  for (let i = 0; i < vertexCount; i += 3) {
    vA.fromBufferAttribute(posAttr, i);
    vB.fromBufferAttribute(posAttr, i + 1);
    vC.fromBufferAttribute(posAttr, i + 2);

    edge1.subVectors(vB, vA);
    edge2.subVectors(vC, vA);
    normal.crossVectors(edge1, edge2);

    const absX = Math.abs(normal.x);
    const absY = Math.abs(normal.y);
    const absZ = Math.abs(normal.z);

    for (let j = 0; j < 3; j++) {
      const idx = i + j;
      const px = posAttr.getX(idx);
      const py = posAttr.getY(idx);
      const pz = posAttr.getZ(idx);

      let u: number;
      let v: number;

      if (absX >= absY && absX >= absZ) {
        u = (py - box.min.y) / sy;
        v = (pz - box.min.z) / sz;
      } else if (absY >= absX && absY >= absZ) {
        u = (px - box.min.x) / sx;
        v = (pz - box.min.z) / sz;
      } else {
        u = (px - box.min.x) / sx;
        v = (py - box.min.y) / sy;
      }

      v = 1 - v;

      uvs[idx * 2] = u;
      uvs[idx * 2 + 1] = v;
    }
  }

  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
}

// ---------------------------------------------------------------------------
// Face-constrained UV generation — pure-JS fallback
// ---------------------------------------------------------------------------

function _generateFaceConstrainedUVsJS(
  geometry: THREE.BufferGeometry,
  posAttr: THREE.BufferAttribute | THREE.InterleavedBufferAttribute,
  vertexCount: number,
  box: THREE.Box3,
  sx: number,
  sy: number,
  sz: number,
  faceAxis: number,
  faceSign: number,
  uvAttrName: string,
): void {
  const uvs = new Float32Array(vertexCount * 2);

  const vA = new THREE.Vector3();
  const vB = new THREE.Vector3();
  const vC = new THREE.Vector3();
  const edge1 = new THREE.Vector3();
  const edge2 = new THREE.Vector3();
  const normal = new THREE.Vector3();

  for (let i = 0; i < vertexCount; i += 3) {
    vA.fromBufferAttribute(posAttr, i);
    vB.fromBufferAttribute(posAttr, i + 1);
    vC.fromBufferAttribute(posAttr, i + 2);

    edge1.subVectors(vB, vA);
    edge2.subVectors(vC, vA);
    normal.crossVectors(edge1, edge2);

    const absX = Math.abs(normal.x);
    const absY = Math.abs(normal.y);
    const absZ = Math.abs(normal.z);

    // Determine dominant axis and sign of this face
    let dominantAxis: number;
    let dominantSign: number;
    if (absX >= absY && absX >= absZ) {
      dominantAxis = 0;
      dominantSign = normal.x >= 0 ? 1 : -1;
    } else if (absY >= absX && absY >= absZ) {
      dominantAxis = 1;
      dominantSign = normal.y >= 0 ? 1 : -1;
    } else {
      dominantAxis = 2;
      dominantSign = normal.z >= 0 ? 1 : -1;
    }

    const matches = dominantAxis === faceAxis && dominantSign === faceSign;

    for (let j = 0; j < 3; j++) {
      const idx = i + j;

      if (!matches) {
        // Non-matching face: UV (0, 0) → transparent corner
        uvs[idx * 2] = 0;
        uvs[idx * 2 + 1] = 0;
        continue;
      }

      // Matching face: normal box-projection UV in [0, 1]
      const px = posAttr.getX(idx);
      const py = posAttr.getY(idx);
      const pz = posAttr.getZ(idx);

      let u: number;
      let v: number;

      if (dominantAxis === 0) {
        // X-dominant: project onto YZ plane
        u = (py - box.min.y) / sy;
        v = (pz - box.min.z) / sz;
      } else if (dominantAxis === 1) {
        // Y-dominant: project onto XZ plane
        u = (px - box.min.x) / sx;
        v = (pz - box.min.z) / sz;
      } else {
        // Z-dominant: project onto XY plane
        u = (px - box.min.x) / sx;
        v = (py - box.min.y) / sy;
      }

      v = 1 - v;

      uvs[idx * 2] = u;
      uvs[idx * 2 + 1] = v;
    }
  }

  geometry.setAttribute(uvAttrName, new THREE.Float32BufferAttribute(uvs, 2));
}
